import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import { canSalesTarget } from './salesTargetAccess';
import CreateSalesmanModal from './modals/CreateSalesmanModal';
import BulkUploadSalesmenModal from './modals/BulkUploadSalesmenModal';
import EditSalesmanModal from './modals/EditSalesmanModal';
import './SalesmenTab.css';

// ── Icon SVGs ────────────────────────────────────────────────
const IconPlus = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);
const IconUpload = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
);
const IconEdit = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const IconDelete = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);
const IconRefresh = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);
const IconSearch = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="M21 21l-4.35-4.35"/>
    </svg>
);

const SalesmenTab = () => {
    const { user } = useAuth();
    // The tab opens for 'create' OR 'edit'; each button needs its own action,
    // matching the method the server maps it to.
    const canCreate = canSalesTarget(user, 'create');
    const canEdit = canSalesTarget(user, 'edit');
    const canDelete = canSalesTarget(user, 'delete');
    const [salesmen, setSalesmen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);

    const [filters, setFilters] = useState({
        companyId: '',
        branchId: '',
        active: '',
        search: ''
    });

    // Debounced search state
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedSalesman, setSelectedSalesman] = useState(null);

    useEffect(() => { fetchCompanies(); fetchBranches(); }, []);
    
    // Debounce search input (400ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
        }, 400);

        return () => clearTimeout(timer);
    }, [searchInput]);

    // Update filters when debounced search changes
    useEffect(() => {
        setFilters(prev => ({ ...prev, search: debouncedSearch }));
    }, [debouncedSearch]);

    useEffect(() => { fetchSalesmen(); }, [filters]);
    useEffect(() => { fetchBranches(); }, [filters.companyId]);

    const fetchCompanies = async () => {
        try {
            const res = await axiosInstance.get('/api/sales-target/organizations/tree');
            if (res.data.success) {
                const extract = (nodes) => {
                    let list = [];
                    nodes.forEach(n => {
                        if (n.type === 'company') list.push(n);
                        if (n.children) list = [...list, ...extract(n.children)];
                    });
                    return list;
                };
                setCompanies(extract(res.data.data || []));
            }
        } catch (err) {
            console.error('Failed to fetch companies:', err);
        }
    };

    const fetchBranches = async () => {
        try {
            const res = await axiosInstance.get('/api/sales-target/organizations/tree');
            if (res.data.success) {
                const extract = (nodes, parentCompanyId = null) => {
                    let list = [];
                    nodes.forEach(n => {
                        const companyId = n.type === 'company' ? n._id : parentCompanyId;
                        if (n.type === 'branch') {
                            if (!filters.companyId || parentCompanyId === filters.companyId) {
                                list.push(n);
                            }
                        }
                        if (n.children) list = [...list, ...extract(n.children, companyId)];
                    });
                    return list;
                };
                setBranches(extract(res.data.data || []));
            }
        } catch (err) {
            console.error('Failed to fetch branches:', err);
        }
    };

    const fetchSalesmen = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {};
            if (filters.companyId) params.companyId = filters.companyId;
            if (filters.branchId) params.branchId = filters.branchId;
            if (filters.active !== '') params.active = filters.active;
            if (filters.search) params.search = filters.search;

            const res = await axiosInstance.get('/api/sales-target/salesmen', { params });
            if (res.data.success) setSalesmen(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load salesmen');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (salesman) => {
        setSelectedSalesman(salesman);
        setShowEdit(true);
    };

    const handleDelete = async (salesmanId) => {
        if (!window.confirm('Delete this salesman? This action cannot be undone.')) return;
        try {
            await axiosInstance.delete(`/api/sales-target/salesmen/${salesmanId}`);
            fetchSalesmen();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete salesman');
        }
    };

    const setFilter = (key, val) => {
        setFilters(prev => {
            const next = { ...prev, [key]: val };
            if (key === 'companyId') { next.branchId = ''; }
            return next;
        });
    };

    const totalSalesmen = salesmen.length;
    const activeSalesmen = salesmen.filter(s => s.active).length;

    // Loading state
    if (loading && salesmen.length === 0) {
        return (
            <div className="salesmen-tab">
                <div className="salesmen-loading">
                    <div className="spinner" />
                    <p>Loading salesmen…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="salesmen-tab">
            {/* Header */}
            <div className="salesmen-header">
                <h2>Salesmen Management</h2>
                {canCreate && (
                    <div className="action-buttons">
                        <button className="btn-secondary" onClick={() => setShowBulkUpload(true)}>
                            <IconUpload /> Bulk Upload
                        </button>
                        <button className="btn-primary" onClick={() => setShowCreate(true)}>
                            <IconPlus /> Add Salesman
                        </button>
                    </div>
                )}
            </div>

            {/* Summary cards */}
            {salesmen.length > 0 && (
                <div className="salesmen-summary-cards">
                    <div className="summary-card">
                        <span className="sc-label">Total Salesmen</span>
                        <span className="sc-value">{totalSalesmen}</span>
                    </div>
                    <div className="summary-card">
                        <span className="sc-label">Active Salesmen</span>
                        <span className="sc-value">{activeSalesmen}</span>
                    </div>
                    <div className="summary-card">
                        <span className="sc-label">Inactive Salesmen</span>
                        <span className="sc-value">{totalSalesmen - activeSalesmen}</span>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="salesmen-filters">
                {/* Company — always first */}
                <div className="filter-group">
                    <label>Company</label>
                    <select value={filters.companyId} onChange={(e) => setFilter('companyId', e.target.value)}>
                        <option value="">All Companies</option>
                        {companies.map(c => (
                            <option key={c._id} value={c._id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Branch</label>
                    <select value={filters.branchId} onChange={(e) => setFilter('branchId', e.target.value)}>
                        <option value="">All Branches</option>
                        {branches.map(branch => (
                            <option key={branch._id} value={branch._id}>{branch.name}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Status</label>
                    <select value={filters.active} onChange={(e) => setFilter('active', e.target.value)}>
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>

                <div className="filter-group search-group">
                    <label>Search</label>
                    <div className="search-input">
                        <IconSearch />
                        <input
                            type="text"
                            placeholder="Search by name, email, phone, or employee ID..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                </div>

                <button className="refresh-btn" onClick={fetchSalesmen} title="Refresh">
                    <IconRefresh /> Refresh
                </button>
            </div>

            {/* Error */}
            {error && <div className="error-message">⚠ {error}</div>}

            {/* Table */}
            <div className="salesmen-table-container">
                {salesmen.length === 0 ? (
                    <div className="empty-state">
                        <p>No salesmen found for the selected filters.</p>
                        {canCreate && (
                            <div className="empty-state-buttons">
                                <button className="btn-secondary" onClick={() => setShowBulkUpload(true)}>
                                    <IconUpload /> Bulk Upload
                                </button>
                                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                                    <IconPlus /> Add First Salesman
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <table className="salesmen-table">
                        <thead>
                            <tr>
                                <th>Employee ID</th>
                                <th>Name</th>
                                <th>Company</th>
                                <th>Branch</th>
                                <th>Contact</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salesmen.map((salesman) => (
                                <tr key={salesman._id}>
                                    <td>
                                        <span className="employee-id">{salesman.employeeId}</span>
                                    </td>
                                    <td>
                                        <div className="salesman-info">
                                            <span className="salesman-name">{salesman.name}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="company-name-cell">{salesman.companyName || '—'}</span>
                                    </td>
                                    <td>
                                        <span className="branch-name">{salesman.branchName}</span>
                                    </td>
                                    <td>
                                        <div className="contact-info">
                                            {salesman.phone && (
                                                <div className="contact-item">📞 {salesman.phone}</div>
                                            )}
                                            {salesman.email && (
                                                <div className="contact-item">✉️ {salesman.email}</div>
                                            )}
                                            {!salesman.phone && !salesman.email && (
                                                <span className="no-contact">No contact info</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${salesman.active ? 'active' : 'inactive'}`}>
                                            {salesman.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="date-info">
                                            <div>{new Date(salesman.createdAt).toLocaleDateString()}</div>
                                            <small>{new Date(salesman.createdAt).toLocaleTimeString()}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            {canEdit && (
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(salesman)}
                                                    title="Edit"
                                                >
                                                    <IconEdit />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    className="btn-icon delete"
                                                    onClick={() => handleDelete(salesman._id)}
                                                    title="Delete"
                                                >
                                                    <IconDelete />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modals */}
            <CreateSalesmanModal
                show={showCreate}
                onClose={() => setShowCreate(false)}
                onSuccess={fetchSalesmen}
                branches={branches}
            />
            <BulkUploadSalesmenModal
                show={showBulkUpload}
                onClose={() => setShowBulkUpload(false)}
                onSuccess={fetchSalesmen}
                branches={branches}
            />
            <EditSalesmanModal
                show={showEdit}
                salesman={selectedSalesman}
                onClose={() => setShowEdit(false)}
                onSuccess={fetchSalesmen}
                branches={branches}
            />
        </div>
    );
};

export default SalesmenTab;